import { useGLTF } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { useEffect, useRef, useState } from 'react';
import { AnimationMixer, Clock, Raycaster, Vector2, Vector3 } from 'three';
import { gsap } from 'gsap';
import useIsMobile from './hooks/useIsMobile'; // Adjust path as needed


export default function Experience() {
const isMobile = useIsMobile();

  const { scene, animations } = useGLTF('/jotanewnew12.glb');
  const { camera, gl, set } = useThree();

  const mixerRef = useRef(null);
  const raycaster = useRef(new Raycaster());
  const mouse = useRef(new Vector2());
  const hoveredTextRef = useRef(null);
  const yoyoMotion = useRef(null);
  const originalCameraPos = useRef(new Vector3());
  const zoomedCameraPos = useRef(null);

  const [zoomedIn, setZoomedIn] = useState(false);
  const [canClickObjects, setCanClickObjects] = useState(false);

useEffect(() => {
  const cameraNode = scene.getObjectByName('Camera');

  if (cameraNode) {

    const mobilePos = new Vector3(20, 0.3, 0.8);
    const desktopPos = cameraNode.position.clone();

    // Set camera position based on device
    if (isMobile) {
      cameraNode.position.copy(mobilePos);
    }

    // 🔧 Fix squished screen: ensure correct aspect ratio & renderer size
    cameraNode.aspect = window.innerWidth / window.innerHeight;
    cameraNode.updateProjectionMatrix();
    gl.setSize(window.innerWidth, window.innerHeight);

    originalCameraPos.current.copy(cameraNode.position.clone());
    set({ camera: cameraNode });
  }

  // 🎞 Play model animations
  if (animations.length > 0) {
    const mixer = new AnimationMixer(scene);
    mixerRef.current = mixer;
    const clock = new Clock();

    animations.forEach((clip) => {
      const action = mixer.clipAction(clip);
      action.play();
      action.timeScale = 0.25;
    });

    gl.setAnimationLoop(() => {
      if (!zoomedIn) {
        const delta = clock.getDelta();
        mixer.update(delta);
      }
    });
  }

  // 🎥 Camera yoyo motion when zoomed out
  if (!zoomedIn) {
    yoyoMotion.current = gsap.to(camera.position, {
      x: "+=1",
      y: "+=0.5",
      repeat: -1,
      yoyo: true,
      duration: 7,
      ease: "power1.inOut",
    });
  }

  // 🖥️ Optional: Handle window resize
  const handleResize = () => {
    if (cameraNode) {
      cameraNode.aspect = window.innerWidth / window.innerHeight;
      cameraNode.updateProjectionMatrix();
    }
    gl.setSize(window.innerWidth, window.innerHeight);
  };

  window.addEventListener('resize', handleResize);

  return () => {
    gl.setAnimationLoop(null);
    mixerRef.current?.stopAllAction();
    yoyoMotion.current?.kill();
    window.removeEventListener('resize', handleResize);
  };
}, [scene, animations, gl, set, zoomedIn]);

  useEffect(() => {
    const handleClick = (event) => {
      if (zoomedIn) return;

      mouse.current.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.current.y = -(event.clientY / window.innerHeight) * 2 + 1;

      raycaster.current.setFromCamera(mouse.current, camera);
      const intersects = raycaster.current.intersectObjects(scene.children, true);

      if (intersects.length > 0) {
        const clickedObject = intersects[0].object;
        if (clickedObject.name.includes('pegboard')) {
          yoyoMotion.current?.kill();
          mixerRef.current?.stopAllAction();
          zoomToPegboard(clickedObject);
          setZoomedIn(true);
        }
      }
    };

    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [scene, camera, zoomedIn]);



const zoomToPegboard = (object) => {
  const pegboardPosition = new Vector3();
  object.getWorldPosition(pegboardPosition);



  const targetPosition = new Vector3(
    pegboardPosition.x + (isMobile ? 40 : 30),  // mobile closer in x
    pegboardPosition.y + (isMobile ? 0.4 : 0.3),
    pegboardPosition.z + (isMobile ? 1.5 : 0.8) // mobile slightly further in z
  );

  zoomedCameraPos.current = targetPosition.clone();

  gsap.to(camera.position, {
    x: targetPosition.x,
    y: targetPosition.y,
    z: targetPosition.z,
    duration: 1.5,
    ease: "power2.inOut",
    onUpdate: () => camera.lookAt(pegboardPosition),
    onComplete: () => setCanClickObjects(true)
  });
};


  const resetCamera = () => {
    setCanClickObjects(false);

    gsap.to(camera.position, {
      x: originalCameraPos.current.x,
      y: originalCameraPos.current.y,
      z: originalCameraPos.current.z,
      duration: 1.5,
      ease: "power2.inOut",
      onUpdate: () => camera.lookAt(0, 0, 0),
      onComplete: () => {
        yoyoMotion.current = gsap.to(camera.position, {
          x: "+=1",
          y: "+=0.5",
          repeat: -1,
          yoyo: true,
          duration: 7,
          ease: "power1.inOut"
        });
        setZoomedIn(false);
      }
    });
  };

  useEffect(() => {
    if (!canClickObjects) return;

    const handleObjectClick = (event) => {
      mouse.current.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.current.y = -(event.clientY / window.innerHeight) * 2 + 1;

      raycaster.current.setFromCamera(mouse.current, camera);
      const intersects = raycaster.current.intersectObjects(scene.children, true);

      if (intersects.length > 0) {
        const clickedObject = intersects[0].object;

        const links = {
          'bag1_Baked': 'https://a3a452-ff.myshopify.com/collections/all',
          'bag2_Baked': 'https://a3a452-ff.myshopify.com/products/another-bag',
          'jacket_Baked': 'https://a3a452-ff.myshopify.com/products/patch-jacket',
          'hat1_Baked': 'https://a3a452-ff.myshopify.com/products/patch-hat-01',
          'doubleBag_Baked': 'https://a3a452-ff.myshopify.com/products/shmall-bag',
          'Sphere': 'https://a3a452-ff.myshopify.com/collections/all',
          'Text': 'https://a3a452-ff.myshopify.com/collections/all',
          'salaPoster_Baked': 'https://staysilentpvd.com'
        };

        if (clickedObject.name === 'Text2') {
          resetCamera();
        } else if (links[clickedObject.name]) {
          window.location.href = links[clickedObject.name];
        }
      }
    };

    window.addEventListener('click', handleObjectClick);
    return () => window.removeEventListener('click', handleObjectClick);
  }, [scene, camera, canClickObjects]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      mouse.current.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.current.y = -(event.clientY / window.innerHeight) * 2 + 1;

      raycaster.current.setFromCamera(mouse.current, camera);
      const intersects = raycaster.current.intersectObjects(scene.children, true);

      if (intersects.length > 0) {
        const hoveredObj = intersects[0].object;
        const hovered = hoveredObj.name;

        const clickable = [
          'bag1_Baked',
          'bag2_Baked',
          'jacket_Baked',
          'hat1_Baked',
          'doubleBag_Baked',
          'salaPoster_Baked',
          'Sphere',
          'Text',
          'Text2'
        ];

        document.body.style.cursor = clickable.includes(hovered) ? 'pointer' : 'default';

        if (hovered === 'Text') {
          if (hoveredTextRef.current !== hoveredObj) {
            if (hoveredTextRef.current) {
              hoveredTextRef.current.material.emissive?.set('#000000');
            }
            hoveredTextRef.current = hoveredObj;
            hoveredTextRef.current.material.emissive?.set('#ffff00');
          }
        } else if (hoveredTextRef.current) {
          hoveredTextRef.current.material.emissive?.set('#000000');
          hoveredTextRef.current = null;
        }


        // Fade in/out Text2 on hover
        if (hovered === 'Text2') {
          hoveredObj.material.transparent = true;
          hoveredObj.material.opacity = zoomedIn ? 1 : 0;
        }
      } else {
        document.body.style.cursor = 'default';
        if (hoveredTextRef.current) {
          hoveredTextRef.current.material.emissive?.set('#000000');
          hoveredTextRef.current = null;
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (hoveredTextRef.current) {
        hoveredTextRef.current.material.emissive?.set('#000000');
        hoveredTextRef.current = null;
      }
    };
  }, [scene, camera, zoomedIn]);

  return (
    <>
      <color args={['#ffffff']} attach="background" />
      <ambientLight intensity={0.5} />
      <directionalLight position={[5, 5, 5]} intensity={1} />
      <primitive object={scene} />
    </>
  );
}
